import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { caseActions } from "@_actions/case.actions";
import { casesActions } from "@_actions/cases.actions";
import { emailConstants } from "@_constants/email_flows.constants";

/**
 * Owns all reminder-related state and handlers for MailPopup: the
 * reminder menu, date picker, name, the "set reminder" checkbox and the
 * deadline persistence (saveDeadline). Extracted from MailPopup with the
 * behavior preserved, plus one fix:
 *
 *  - `doSaveDeadline` defaults to `!isReplyMode`. Previously this was only
 *    read on the first render, so the checkbox stayed stale if `isReplyMode`
 *    flipped after mount. It is now kept in sync via an effect that re-runs
 *    only when `isReplyMode` actually changes, so a user's manual toggle is
 *    preserved while a mode switch resets it correctly.
 */
export function useReminderState({ caseId, caseData, dataName, isReplyMode }) {
  const dispatch = useDispatch();

  const [reminderMenuOpen, setReminderMenuOpen] = useState(null);
  const [reminderSetting, setReminderSetting] = useState("in 7 days");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [reminderDate, setReminderDate] = useState(null);
  const [doSaveDeadline, setDoSaveDeadline] = useState(
    isReplyMode ? false : true,
  );
  const [reminderName, setReminderName] = useState("Follow Up: ");
  const [showAreYouSurePopup, setShowAreYouSurePopup] = useState(false);
  const [deleteReminder, setDeleteReminder] = useState(false);

  // Calculate minDate for reminder date picker (tomorrow)
  const reminderMinDate = useMemo(() => {
    return moment().add(1, "days");
  }, []);

  // Keep doSaveDeadline in sync with isReplyMode. Runs only when isReplyMode
  // changes, so the user's manual checkbox choice is not clobbered while the
  // popup stays in a single mode.
  useEffect(() => {
    setDoSaveDeadline(!isReplyMode);
  }, [isReplyMode]);

  /**
   * Key of autocomplete --> needed to rerender the component after accepting a email
   */
  useEffect(() => {
    if (isReplyMode || dataName === emailConstants.NEW_MAIL)
      setReminderName("Follow up");
    else setReminderName("Follow Up: " + dataName);
  }, [dataName, isReplyMode]);

  const handleOpenReminderMenu = (event) => {
    setReminderMenuOpen(event.currentTarget);
  };

  const handleCloseReminderMenu = () => {
    setReminderMenuOpen(null);
  };

  const handleChangeReminderOption = (numberOrCustom) => {
    if (typeof numberOrCustom === "number") {
      setReminderSetting("in " + numberOrCustom + " days");
      handleCloseReminderMenu();
      setShowDatePicker(false);
    } else {
      // activate datepicker
      setReminderSetting("on");
      handleCloseReminderMenu();
      setShowDatePicker(true);
    }
  };

  const setReminderChecked = (event) => {
    setDoSaveDeadline(Boolean(event.target.checked));
    if (caseData?.reminder_date) {
      setShowAreYouSurePopup(!Boolean(event.target.checked));
    }
  };

  const handleDeleteReminder = () => {
    setDeleteReminder(true);
    setShowAreYouSurePopup(false);
  };

  const handleAreYouSurePopupClose = () => {
    setShowAreYouSurePopup(false);
  };

  function saveDeadline() {
    let dateInput = new Date();
    if (doSaveDeadline) {
      if (reminderSetting === "custom") {
        dateInput = reminderDate;
      } else {
        switch (reminderSetting) {
          case "in 3 days":
            dateInput.setDate(dateInput.getDate() + 3);
            break;
          case "in 5 days":
            dateInput.setDate(dateInput.getDate() + 5);
            break;
          case "in 7 days":
            dateInput.setDate(dateInput.getDate() + 7);
            break;
          case "in 14 days":
            dateInput.setDate(dateInput.getDate() + 14);
            break;
          case "in 30 days":
            dateInput.setDate(dateInput.getDate() + 30);
            break;
          default:
            break;
        }
      }
      dispatch(
        caseActions.updateReminder(caseId, {
          name: reminderName,
          date: dateInput,
        }),
      );
    } else if (deleteReminder) {
      dispatch(caseActions.updateReminder(caseId));
      setDeleteReminder(false);
    }
    setTimeout(() => dispatch(casesActions.getCases()), 500);
    return true;
  }

  return {
    reminderMenuOpen,
    reminderSetting,
    showDatePicker,
    reminderDate,
    setReminderDate,
    doSaveDeadline,
    reminderName,
    setReminderName,
    showAreYouSurePopup,
    reminderMinDate,
    handleOpenReminderMenu,
    handleCloseReminderMenu,
    handleChangeReminderOption,
    setReminderChecked,
    handleDeleteReminder,
    handleAreYouSurePopupClose,
    saveDeadline,
  };
}
